import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "components/button";
import { toast } from "react-toastify";

interface IStatusEntry {
    dateTime: string;
    statusDescription: string;
}

interface IShipmentDetails {
    shippedDate?: string;
    estimatedDeliveryDate?: string;
    shipmentStatus?: string;
    trackingNumber?: string;
}

interface IHeritageMeta {
    transactionId?: string;
    customerOrderId?: string;
    heritageOrderId?: string;
}

const StatusDetails = ({ orderId }: { orderId: number }) => {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [entries, setEntries] = useState<IStatusEntry[]>([]);
    const [shipment, setShipment] = useState<IShipmentDetails | null>(null);
    const [meta, setMeta] = useState<IHeritageMeta>({});

    const fetchStatus = async () => {
        try {
            setLoading(true);
            const res = await axios.get(
                `${process.env.REACT_APP_SERVER_URL}/api/v1/company/heritage/remote-status`,
                { params: { orderId } }
            );

            const { orderStatus, shipmentStatus } = res.data;

            if (orderStatus?.statusHistory) {
                setEntries(
                    orderStatus.statusHistory.map((entry: any) => ({
                        dateTime: entry.dateTime,
                        statusDescription: entry.statusDescription,
                    }))
                );
            } else {
                setEntries([]);
            }

            if (shipmentStatus?.shipmentDetails) {
                const containers = shipmentStatus.containers ?? [];
                const tracking = containers[0]?.trackingNumber ?? null;

                setShipment({
                    shippedDate: shipmentStatus.shipmentDetails.shippedDate,
                    estimatedDeliveryDate: shipmentStatus.shipmentDetails.estimatedDeliveryDate,
                    shipmentStatus: shipmentStatus.shipmentDetails.shipmentStatus,
                    trackingNumber: tracking,
                });
            } else {
                setShipment(null);
            }

            setMeta({
                transactionId: orderStatus?.transactionId || shipmentStatus?.transactionId,
                customerOrderId: orderStatus?.customerOrderId || shipmentStatus?.customerOrderId,
                heritageOrderId: orderStatus?.heritageOrderId || shipmentStatus?.heritageOrderId,
            });

            toast.success("Order status refreshed");
        } catch (err) {
            console.error("Failed to fetch status", err);
            toast.error("Failed to fetch order status");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) fetchStatus();
    }, [open]);

    return (
        <>
            <Button className="ml-4" onClick={() => setOpen(true)}>
                Status
            </Button>

            {open && (
                <div className="fixed inset-0 z-50 bg-black bg-opacity-30 flex items-center justify-center">
                    <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl p-6 relative">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-lg font-semibold">Order Status Details</h2>
                            <button className="text-gray-500 hover:text-gray-700" onClick={() => setOpen(false)}>
                                ✕
                            </button>
                        </div>

                        <div className="mb-4 space-y-1 text-sm text-gray-700">
                            {meta.customerOrderId && <div><strong>Customer Order ID:</strong> {meta.customerOrderId}</div>}
                            {meta.heritageOrderId && <div><strong>Heritage Order ID:</strong> {meta.heritageOrderId}</div>}
                            {meta.transactionId && <div><strong>Transaction ID:</strong> {meta.transactionId}</div>}
                        </div>

                        <div className="mb-4">
                            <Button onClick={fetchStatus}>Refresh</Button>
                        </div>

                        {loading ? (
                            <div className="text-sm text-gray-500">Loading order status...</div>
                        ) : (
                            <>
                                {shipment && (
                                    <div className="bg-gray-50 border rounded p-4 text-sm space-y-1 mb-4">
                                        <div><strong>Shipped:</strong> {shipment.shippedDate ?? "N/A"}</div>
                                        <div><strong>Est. Delivery:</strong> {shipment.estimatedDeliveryDate ?? "N/A"}</div>
                                        <div><strong>Shipment Status:</strong> {shipment.shipmentStatus ?? "N/A"}</div>
                                        {shipment.trackingNumber && (
                                            <div><strong>Tracking #:</strong> {shipment.trackingNumber}</div>
                                        )}
                                    </div>
                                )}

                                {entries.length > 0 ? (
                                    <table className="min-w-full bg-white text-sm border rounded">
                                        <thead>
                                            <tr>
                                                <th className="text-left py-2 px-4 border-b text-gray-500">Date/Time</th>
                                                <th className="text-left py-2 px-4 border-b text-gray-500">Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {entries.map((entry, i) => (
                                                <tr key={i}>
                                                    <td className="py-2 px-4 border-b">{entry.dateTime}</td>
                                                    <td className="py-2 px-4 border-b">{entry.statusDescription}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                ) : (
                                    <div className="text-sm text-gray-500">No status updates available.</div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default StatusDetails;
